/**
 * Admin Config — Domain Types
 *
 * Generic key-value configuration system with typed JSON payloads.
 * Each config key maps to a specific TypeScript type via ConfigKeyMap.
 */

// =============================================================================
// Domain Model
// =============================================================================

export interface AdminConfig<T = unknown> {
  id: string;
  key: string;
  category: string;
  displayName: string;
  description?: string;
  value: T;
  rowVersion?: string;
  modifiedOn?: string;
}

export interface UpdateAdminConfigInput<T = unknown> {
  value: T;
}

// =============================================================================
// Config Key Registry
// =============================================================================

/**
 * Maps config keys to their typed JSON payloads.
 * Add new keys here as new configs are introduced.
 */
export interface ConfigKeyMap {
  'sales.stages': SalesStagesValue;
  'sales.solutionCategories': SolutionCategoriesValue;
  'sales.salesYear': SalesYearValue;
  'sales.repTeams': RepTeamsValue;
  'sales.outreachTemplate': OutreachTemplateValue;
  'sales.pointsMatrix': PointsMatrixValue;
}

export type ConfigKey = keyof ConfigKeyMap;

// =============================================================================
// Sales Stages Config Shape
// =============================================================================

export interface SalesStageConfig {
  name: string;
  isCase: boolean;
  isPipeline: boolean;
  color: string | null;
  order: number;
  /** If set, opportunities at this stage are treated as being at the mapped stage (for reporting only) */
  mappedTo?: string;
  /** BPF stage GUID — links the stage to rows in dts_veracitybpfstagechangelog */
  bpfStageId?: string;
}

export interface SalesStagesValue {
  stages: SalesStageConfig[];
}

// =============================================================================
// Solution Categories Config Shape
// =============================================================================

export interface SolutionCategoryConfig {
  /** Display name shown in reports (e.g., "Health Plan Solutions") */
  name: string;
  /** Dataverse field names that belong to this category */
  fields: string[];
  /** Display order */
  order: number;
  /** Color for charts */
  color: string | null;
}

export interface SolutionCategoriesValue {
  categories: SolutionCategoryConfig[];
  /** Maps field name → display label for individual product toggles */
  fieldLabels: Record<string, string>;
}

// =============================================================================
// Sales Year Config Shape
// =============================================================================

export interface SalesYearValue {
  /** Sales year start month (1-12). Default 1 = January (calendar year). */
  startMonth: number;
}

// =============================================================================
// Rep Teams Config Shape
// =============================================================================

export interface RepTeamConfig {
  /** Display name shown in reports (e.g., "M.P.MV") */
  name: string;
  /** Dataverse ownerIds (systemuser GUIDs) that belong to this team */
  memberIds: string[];
  /** Optional display order */
  order: number;
}

export interface RepTeamsValue {
  teams: RepTeamConfig[];
}

// =============================================================================
// Email Templates Config Shape
// =============================================================================

export interface EmailTemplate {
  /** Email subject template with curly-brace tokens. */
  subject: string;
  /** Email body HTML template with curly-brace tokens. */
  body: string;
}

export interface OutreachTemplateValue {
  /** Named email templates keyed by type (e.g., "oneCard", "happyBirthday"). */
  templates: Record<string, EmailTemplate>;
}

// =============================================================================
// Quick Activity Points — Points Matrix Config Shape
// =============================================================================

/**
 * One row of the points matrix per scoring event. The first five names match
 * SalesActivityFeedItem activityType + appointmentSubtype:
 *   email                     → activityType=email
 *   phonecall                 → activityType=phonecall
 *   appointmentVirtual        → activityType=appointment, subtype=virtual
 *   appointmentFaceToFace     → activityType=appointment, subtype=face-to-face
 *   appointmentMeal           → activityType=appointment, subtype=meal
 *
 * The two proposal events are fired by the BPF stage-change log
 * (`dts_veracitybpfstagechangelog`) when an opportunity transitions into a
 * specific case stage:
 *   proposalCreated    — entered "Proposal Creation For Open Case"
 *                        (opportunity moved into the proposal pipeline)
 *   proposalPresented  — entered "Presentation"
 *                        (proposal presented to broker / broker + group)
 *
 * Both are weighted separately by the opportunity's broker classification.
 */
export type ActivityPointKey =
  | 'email'
  | 'phonecall'
  | 'appointmentVirtual'
  | 'appointmentFaceToFace'
  | 'appointmentMeal'
  | 'proposalCreated'
  | 'proposalPresented';

export interface PointsScheduleWeights {
  email: { existing: number; prospect: number };
  phonecall: { existing: number; prospect: number };
  appointmentVirtual: { existing: number; prospect: number };
  appointmentFaceToFace: { existing: number; prospect: number };
  appointmentMeal: { existing: number; prospect: number };
  proposalCreated: { existing: number; prospect: number };
  proposalPresented: { existing: number; prospect: number };
}

export interface PointsSchedule {
  /** Stable id for editor row tracking. Generated client-side, not used for any keying. */
  id: string;
  /** First date this schedule applies (YYYY-MM-DD, inclusive). */
  startDate: string;
  /** Last date this schedule applies (YYYY-MM-DD, inclusive). null => open-ended. */
  endDate: string | null;
  weights: PointsScheduleWeights;
}

export interface PointsMatrixValue {
  /**
   * How to classify an activity targeting a broker we have no record for.
   * Most teams credit unknowns as Prospect activity (lower value, conservative)
   * but some prefer Existing (higher value, encourages logging anything).
   */
  unknownBrokerDefault: 'existing' | 'prospect';
  schedules: PointsSchedule[];
}

// =============================================================================
// Guide Article Config Shape
// =============================================================================

/**
 * Modules where a guide article can be surfaced. Each module's Guide page
 * filters to articles whose `modules` array includes its module key. The
 * shell's own `/guide` page uses 'shell' for platform-level docs.
 */
export const GUIDE_MODULES = ['shell', 'sales', 'ops', 'catalog'] as const;
export type GuideModule = (typeof GUIDE_MODULES)[number];

/**
 * In-app help article. Stored as a `vba_adminconfig` row with category=`guide`
 * and a key like `guide.access`, `guide.onboarding`, etc.
 *
 * Visibility:
 *   - 'all'   → any authenticated user can read it (sales reps, etc.)
 *   - 'admin' → only global Admins can read it (operational/admin docs)
 *
 * Edit affordance is always restricted to global Admin regardless of visibility.
 */
export interface GuideArticleValue {
  /** URL-safe identifier (matches the key suffix after `guide.`) — used for in-page navigation. */
  slug: string;
  /** Display title shown in the sidebar and as the article heading. */
  title: string;
  /** Markdown body. */
  body: string;
  /** Display order in the sidebar (ascending, global across modules). */
  order: number;
  /** Who can read this article. */
  visibility: 'all' | 'admin';
  /** Modules this article appears in. Empty/missing is treated as `['shell']` (legacy default). */
  modules: GuideModule[];
  /**
   * Optional group label. Articles sharing a non-empty group string are
   * clustered under a heading in the sidebar. Empty/missing = ungrouped
   * (rendered without a heading).
   */
  group?: string;
  /**
   * Routes (exact pathname match) that should display a help (?) icon
   * pointing at this article. e.g. `['/sales/one-card']`. The `<PageHelp>`
   * widget renders the icon next to the page title when the current
   * pathname matches one of these entries; otherwise the icon is hidden.
   * Multiple routes may share one article.
   */
  helpForRoutes?: string[];
}
